/**
 * Language strings, loaded one language at a time.
 *
 * Both dictionaries used to sit in this module, so every visitor downloaded and parsed every
 * language whether or not they would ever read it. Measured on 6 Sep 2026: `en` is 72 KB raw /
 * 18.7 KB brotli, `hi` is 126 KB raw / 22.2 KB brotli — Devanagari is multi-byte, so Hindi costs
 * more on the wire and much more to parse. Against a 237 KB brotli main chunk, shipping the
 * language nobody in the session will read is ~8-9% of the download and up to 126 KB of parse work
 * on exactly the low-end phones this product is for.
 *
 * So each language is its own chunk, fetched during boot. Boot already makes four sequential API
 * calls before anything renders, so the chunk fetch overlaps with work that was happening anyway
 * and costs approximately nothing in wall-clock — and once fetched it is served with the
 * `immutable` caching the build applies to all hashed assets.
 *
 * `translate` stays SYNCHRONOUS and keeps its signature. Every call site — `t(key)` in every
 * component — is unchanged, which is the point: making translation async would have made the most
 * cross-cutting function in the app await, for a saving that does not justify touching every
 * render path. Instead the loaded dictionaries live in a module-level cache, and `AppContext`
 * guarantees the active language is loaded before it clears `booting`.
 *
 * Before any load completes `translate` returns the key. That is reachable only if a caller
 * renders before boot finishes, which the app's own `booting` gate prevents — and a visible key
 * beats a thrown TypeError from a component that was mid-render.
 */

const loaders = {
  en: () => import("./en"),
  hi: () => import("./hi"),
};

/** lang -> dictionary object, once loaded. Module-level so it survives re-renders. */
const cache = {};

/** In-flight promises, so two callers asking for the same language share one fetch. */
const pending = {};

export const languageOptions = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिंदी" },
];

// Small enough to stay in the main bundle, and needed by the header before any dictionary has
// necessarily loaded — splitting these would buy nothing and cost a load-order problem.
export const roleLabels = {
  en: {
    citizen: "Citizen",
    business_owner: "Business owner",
    worker: "Worker",
    operator: "City operator",
    platform_admin: "Platform admin",
  },
  hi: {
    citizen: "नागरिक",
    business_owner: "व्यापार मालिक",
    worker: "कारीगर",
    operator: "शहर संचालक",
    platform_admin: "प्लेटफॉर्म प्रशासक",
  },
};

/** True once `lang` can be translated without a fetch. */
export function isDictionaryLoaded(lang) {
  return Boolean(cache[lang]);
}

/**
 * Fetch one language's chunk, at most once. An unknown code falls back to English rather than
 * rejecting: a stale `localStorage` value for a language that was removed must not brick boot.
 */
export async function loadDictionary(lang) {
  const code = loaders[lang] ? lang : "en";
  if (cache[code]) return cache[code];
  if (!pending[code]) {
    pending[code] = loaders[code]()
      .then((mod) => {
        cache[code] = mod.default;
        return cache[code];
      })
      .finally(() => {
        delete pending[code];
      });
  }
  return pending[code];
}

/**
 * Pure: the dictionary is passed IN rather than looked up from the cache.
 *
 * The first version of this read the module cache and took `lang`, which meant React could not see
 * when a translation became available — the cache is invisible to it — so `AppContext` had to carry
 * a version counter and list it as a `useCallback` dependency it never read. `exhaustive-deps` was
 * right to object, and suppressing it would have been arguing with a rule that was correct.
 * Threading the dictionary through makes the data flow explicit and the dependency genuine.
 *
 * `dict` is null until the first chunk lands, and then every key returns itself. That is reachable
 * only if something renders before boot clears, which the `booting` gate prevents — and a visible
 * key beats a TypeError thrown from inside a component's render.
 */
export function translate(dict, key, vars = {}) {
  if (!dict) return key;
  let value = key
    .split(".")
    .reduce((acc, part) => (acc ? acc[part] : undefined), dict);
  if (value === undefined) return key;
  // Pluralisation: a value shaped `{ one, other }` picks `one` only when `count === 1`, else
  // `other` (which also covers 0 and a missing count). English two-form is enough for both
  // languages we ship. Any other non-string value is returned as-is, unchanged from before.
  //
  // BOTH keys must be present, not either — `"other" in value` alone used to fire on any plain
  // label map that happens to have an option literally called "other" (`profile.genderLabels`,
  // `owner.expenseCategories`, `owner.plan.itemType` all do: "Other" is a real, common option
  // label, not a plural form). That silently replaced the whole object with the single string
  // "Other", so `genderLabels[key]` etc. read a property off a string and rendered nothing. A
  // genuine plural shape always defines both forms; nothing in this codebase writes one without
  // the other (grep `dictionaries.test.js` §pluralisation before loosening this again).
  if (value && typeof value === "object" && !Array.isArray(value) && "one" in value && "other" in value) {
    value = vars.count === 1 ? value.one ?? value.other : value.other ?? value.one;
  }
  if (typeof value !== "string") return value;
  return value.replace(/\{(\w+)\}/g, (_, name) =>
    vars[name] !== undefined ? String(vars[name]) : "",
  );
}
